import React from 'react';

import { Button, Modal, Message, Form, ToastContext, SelectHierarchyLabel, RadioLabel } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import { useForm, requiredField } from '../../../utils/validators';
import Organization from '../../../models/organization';
import { LoadingOptions } from '../../../components/loadingCard';

import styles from './duplicateModal.module.css';

import noop from 'lodash/noop';
import isEmpty from 'lodash/isEmpty';
import isArray from 'lodash/isArray';
import useAppContext from '../../../context/useAppContext';
import { usePermissions } from '../../../user/permissions';

const DEFAULT_FORM_VALUES = {
    name: '',
    organization: null
};

const ORGANIZATION_MIN_SEARCH_LENGTH = 3;
const ORGANIZATION_DEFAULT_QUERY_PARAMS = {
    limit: 15,
    offset: 0
};

const ORGANIZATION_TYPES = {
    THIS: 'this',
    ANOTHER: 'another'
};

function OrganizationAutocomplete({ name = 'organization', value = null, disabled = false, error = '', onChange = noop }) {
    const { t } = useTranslation();
    const [{ organization }] = useAppContext();

    const [searchText, setSearchText] = React.useState('');

    const [organizations, isOrganizationsLoading] = Organization.useApiModel(
        {
            cache: false,
            allow_cache: false,
            query: { search: searchText, ...ORGANIZATION_DEFAULT_QUERY_PARAMS },
            launch: searchText.length >= ORGANIZATION_MIN_SEARCH_LENGTH
        },
        [searchText]
    );

    const options = React.useMemo(() => {
        const _getCurrentOrgOption = org => {
            const option = org.toOption();
            return { ...option, text: `${option.text} (${t('ticket.charts.chartlist.duplicateModal.form.organization.currentOrganizationLabel')})` };
        };

        if (searchText.length < ORGANIZATION_MIN_SEARCH_LENGTH) {
            return [
                _getCurrentOrgOption(organization),
                {
                    key: 'info',
                    value: 'info',
                    disabled: true,
                    text: t('ticket.charts.chartlist.duplicateModal.form.organization.searchHelperTextMinLength', {
                        minLength: ORGANIZATION_MIN_SEARCH_LENGTH
                    })
                }
            ];
        }

        if (isOrganizationsLoading || !isArray(organizations)) {
            return LoadingOptions;
        }

        if (!organizations.length) {
            return [
                _getCurrentOrgOption(organization),
                {
                    key: 'no_result',
                    value: 'no_result',
                    disabled: true,
                    text: t('ticket.charts.chartlist.duplicateModal.form.organization.searchHelperTextNoResult')
                }
            ];
        }

        return isOrganizationsLoading || !isArray(organizations)
            ? LoadingOptions
            : organizations.map(org => (Number(org.id) === Number(organization.id) ? _getCurrentOrgOption(org) : org.toOption()));
    }, [searchText, organization, organizations, isOrganizationsLoading]);

    const handleOptionChange = React.useCallback(
        newValue => {
            onChange(null, { name, value: newValue });
        },
        [name, onChange]
    );

    const handleSearchTextChange = React.useCallback(text => {
        setSearchText(text);
    }, []);

    return (
        <div className={styles.form__field_organization_wrapper}>
            <SelectHierarchyLabel
                searchable
                required
                autoComplete="off"
                id="_id_organization"
                name={name}
                value={value}
                disabled={disabled}
                options={options}
                externalOptions={options}
                searchValue={searchText}
                minSearch={ORGANIZATION_MIN_SEARCH_LENGTH}
                errors={error ? [error] : null}
                inputLabel={{ htmlFor: '_id_organization' }}
                onChange={handleOptionChange}
                onExternalSearch={handleSearchTextChange}
                label={t('ticket.charts.chartlist.duplicateModal.form.organization.label')}
                placeholder={t('ticket.common.action.select')}
                placeholderSearch={t('ticket.common.action.search')}
                field={{
                    className: styles.form__field_wrapper__zero_margin
                }}
                fieldErrors={{
                    className: styles.form__field_errors
                }}
            />
        </div>
    );
}

function DuplicateFormBase({ values = {}, errors = {}, onChange = noop }) {
    const { t } = useTranslation();
    const permissions = usePermissions();
    const [{ organization }] = useAppContext();

    const [organizationType, setOrganizationType] = React.useState(ORGANIZATION_TYPES.THIS);

    const hasError = React.useCallback(fieldName => !!(errors && errors[fieldName]), [errors]);
    const getErrorMessage = React.useCallback(fieldName => (hasError(fieldName) ? t(errors[fieldName]) : ''), [errors, hasError]);

    const handleChange = React.useCallback(
        (_, values) => {
            const { name = 'unknow', value = null } = values || {};
            switch (name) {
                case 'name':
                    // Trim spaces at the beginning and the end and limit to 255 characters
                    onChange({ name, value: value.substring(0, 255) });
                    break;
                default:
                    onChange({ name, value });
                    break;
            }
        },
        [onChange]
    );

    const handleChangeOrganizationType = React.useCallback((_, { value: newOrganizationType }) => {
        setOrganizationType(newOrganizationType);
    }, []);

    const isChartAdmin = permissions.hasCapacity('PyvarCapacity.CHART_ADMIN', organization?.id);

    return (
        <Form noValidate className={styles.form}>
            {isChartAdmin && (
                <>
                    <Form.Field className={styles.form__field_wrapper__zero_margin}>
                        <RadioLabel
                            inline
                            bordered
                            name="organizationType"
                            checked={organizationType === ORGANIZATION_TYPES.THIS}
                            value={ORGANIZATION_TYPES.THIS}
                            onChange={handleChangeOrganizationType}
                        >
                            {t(`ticket.charts.chartlist.duplicateModal.form.organizationType.${ORGANIZATION_TYPES.THIS}`)}
                        </RadioLabel>
                        <RadioLabel
                            inline
                            bordered
                            name="organizationType"
                            checked={organizationType === ORGANIZATION_TYPES.ANOTHER}
                            value={ORGANIZATION_TYPES.ANOTHER}
                            onChange={handleChangeOrganizationType}
                        >
                            {t(`ticket.charts.chartlist.duplicateModal.form.organizationType.${ORGANIZATION_TYPES.ANOTHER}`)}
                        </RadioLabel>
                    </Form.Field>

                    {organizationType === ORGANIZATION_TYPES.ANOTHER && (
                        <OrganizationAutocomplete value={values.organization} error={getErrorMessage('organization')} onChange={handleChange} />
                    )}
                </>
            )}

            <Form.Field className={styles.form__field_wrapper__zero_margin}>
                <Form.Label required htmlFor="_id_name" label={t('ticket.charts.chartlist.duplicateModal.form.chartName.label')} />
                <Form.Input id="_id_name" name="name" autoComplete="off" value={values.name} errors={hasError('name')} onChange={handleChange} />
                <Form.FieldErrors errors={[getErrorMessage('name')]} className={styles.form__field_errors} />
            </Form.Field>
        </Form>
    );
}

function DuplicateChartModal({ isOpen = false, initialFormValues = {}, onClose = noop, onSubmit = noop }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const [isSubmitting, setIsSubmitting] = React.useState(false);
    // eslint-disable-next-line no-unused-vars
    const [formValues, setFieldValue, setFormValues, formErrors, validate, setErrors] = useForm(DEFAULT_FORM_VALUES, { name: requiredField, organization: requiredField });

    React.useEffect(() => {
        // Reset form when modal is opened or closed
        setErrors({});
        setFormValues({ ...DEFAULT_FORM_VALUES, ...initialFormValues });
    }, [isOpen]);

    const handleSubmit = React.useCallback(async () => {
        if (isSubmitting || !isEmpty(formErrors)) {
            return;
        }

        setIsSubmitting(true);

        try {
            await onSubmit({ values: formValues });
            toast.success(t('ticket.charts.chartlist.duplicateModal.toast.success'));
            onClose();
        } catch (error) {
            console.error('[DuplicateChartModal] onSubmit error', error);
            toast.error(t('ticket.charts.chartlist.duplicateModal.toast.error'));
        } finally {
            setIsSubmitting(false);
        }
    }, [formValues, isSubmitting, onSubmit]);

    const handleClose = React.useCallback(() => {
        if (isSubmitting) {
            return;
        }
        onClose();
    }, [isSubmitting, onClose]);

    const handleFieldChange = React.useCallback(field => {
        setFieldValue(field.name, field.value);
    }, []);

    return (
        <Modal
            allowExternalClosing={false}
            open={isOpen}
            size="large"
            title={t('ticket.charts.chartlist.duplicateModal.title')}
            subtitle={t('ticket.charts.chartlist.duplicateModal.subtitle')}
            submitButton={
                <Button primary label={t('ticket.common.action.duplicate')} onClick={handleSubmit} disabled={isSubmitting || !isEmpty(formErrors)} isLoading={isSubmitting} />
            }
            cancelButton={<Button onClick={handleClose} label={t('ticket.common.action.cancel')} disabled={isSubmitting} />}
            onClose={handleClose}
        >
            <div>
                <Message type="default" title={t('ticket.charts.chartlist.duplicateModal.information.title')}>
                    {t('ticket.charts.chartlist.duplicateModal.information.description')}
                </Message>

                <DuplicateFormBase values={formValues} errors={formErrors} onChange={handleFieldChange} />
            </div>
        </Modal>
    );
}

DuplicateChartModal.displayName = 'DuplicateChartModal';
export default DuplicateChartModal;
